import { useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { getInitials, getFamilyColor } from '../utils/helpers';

// ── Modal ────────────────────────────────────────────────────
export function Modal({ isOpen, onClose, title, children, large = false }) {
  const overlayRef = useRef();
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div className="modal-overlay" ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
      <div className={`modal-content glass-card${large ? ' modal-lg' : ''}`}>
        {title && (
          <div className="modal-header">
            <h3>{title}</h3>
            <button className="btn-icon modal-close" onClick={onClose}>✕</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────
export function Toast() {
  const { toast } = useApp();
  if (!toast) return null;
  const icons = { success: '✓', error: '✕', info: 'ℹ', warning: '⚠' };
  return (
    <div id="toast-container">
      <div className={`toast toast-${toast.type} toast-show`}>
        <span className="toast-icon">{icons[toast.type] || icons.info}</span>
        <span>{toast.message}</span>
      </div>
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────
export function Avatar({ name, familyId, size = 'md' }) {
  const colors = getFamilyColor(familyId);
  const cls = size === 'sm' ? 'payer-avatar-sm' : 'member-avatar';
  return (
    <div className={cls} style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
      {getInitials(name)}
    </div>
  );
}

// ── Confirm Dialog ────────────────────────────────────────────
export function ConfirmModal({ isOpen, onClose, onConfirm, message }) {
  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="modal-header"><h3>Confirm</h3><button className="btn-icon" onClick={onClose}>✕</button></div>
      <p style={{ margin: '1rem 0 1.5rem' }}>{message}</p>
      <div className="modal-actions">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" style={{ background: 'var(--accent-red-light)' }} onClick={() => { onConfirm(); onClose(); }}>Delete</button>
      </div>
    </Modal>
  );
}

// ── Empty State ───────────────────────────────────────────────
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div className="empty-state glass-card">
      <div className="empty-icon">{icon}</div>
      <h3>{title}</h3>
      {subtitle && <p>{subtitle}</p>}
      {action}
    </div>
  );
}
